import { MetricPanel } from './shared/MetricPanel';
import { useAdminText } from './shared/useAdminText';
import { WorkspaceLinks } from './shared/WorkspaceLinks';
/**
 * Admin Dashboard Component
 * Shows key statistics and metrics
 */

import { useEffect, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import FavoriteIcon from '@mui/icons-material/Favorite';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

import { brand, semantic } from '@rekindle/shared/theme';
import { adminApi } from '../providers/dataProvider';
import { logger } from '../utils/logger';
import { presentRecordedCost } from '../utils/aiCostPresentation';
import { StatCard } from './shared';
import { useAICostQuery } from '../hooks/useAICostQuery';

/**
 * Color palette for dashboard stat cards
 * Uses shared brand and semantic colors
 */
const STAT_COLORS = {
  users: semantic.info,
  newUsers: semantic.success,
  partners: semantic.warning,
  activity: brand.primary,
} as const;

interface Stats {
  users: {
    total: number;
    withProfile: number;
    withPartner: number;
    newThisWeek: number;
    newThisMonth: number;
  };
  subscriptions: Record<string, number>;
  activity: {
    activeToday: number;
    activeThisWeek: number;
    activeThisMonth: number;
  };
}

export function Dashboard() {
  const t = useAdminText();
  const [stats, setStats] = useState<Stats | null>(null);
  const costsQuery = useAICostQuery(['ai-costs', 'stats'], adminApi.getAICostStats);
  const aiCostStats = costsQuery.data;
  const [loading, setLoading] = useState(true);
  const notify = useNotify();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await adminApi.getStats();
        setStats(data);
      } catch (error) {
        notify('Failed to load dashboard stats', { type: 'error' });
        logger.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [notify]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box>
        <Typography color="error">Failed to load dashboard data</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Title title="Overview" />
      <WorkspaceLinks />

      {/* User Statistics */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        User Statistics
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} component="div">
          <StatCard
            title="Total Users"
            value={stats.users.total}
            icon={<PeopleIcon sx={{ color: 'white' }} />}
            color={STAT_COLORS.users}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} component="div">
          <StatCard
            title="New This Week"
            value={stats.users.newThisWeek}
            icon={<PersonAddIcon sx={{ color: 'white' }} />}
            color={STAT_COLORS.newUsers}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} component="div">
          <StatCard
            title="With Partner"
            value={stats.users.withPartner}
            icon={<FavoriteIcon sx={{ color: 'white' }} />}
            color={STAT_COLORS.partners}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} component="div">
          <StatCard
            title="Active This Month"
            value={stats.activity.activeThisMonth}
            icon={<TrendingUpIcon sx={{ color: 'white' }} />}
            color={STAT_COLORS.activity}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <MetricPanel title={t('subscriptionDistribution', 'Subscription distribution')}
            items={Object.entries(stats.subscriptions).map(([tier, count]) => ({label: tier.charAt(0).toUpperCase() + tier.slice(1), value: count}))} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <MetricPanel title={t('communityActivity', 'Community activity')} items={[
            {label:t('activeToday', 'Active today'),value:stats.activity.activeToday},
            {label:t('activeWeek', 'Active this week'),value:stats.activity.activeThisWeek},
            {label:t('withProfile', 'Profiles completed'),value:stats.users.withProfile},
          ]} />
        </Grid>
      </Grid>

      {/* AI Cost Overview */}
      <Box display="flex" alignItems="center" gap={2} mt={4} mb={2}>
        <Typography variant="h6">AI Costs</Typography>
        <Button onClick={() => void costsQuery.refetch()} disabled={costsQuery.isFetching}>Refresh costs</Button>
        <Button href="/ai-costs">View all calls</Button>
      </Box>
      {costsQuery.isError && <Alert severity="error" sx={{ mb: 2 }}>
        {costsQuery.accessDenied ? 'Access to AI costs was denied. Reauthenticate before retrying.' : aiCostStats ? 'Could not refresh costs. Showing the last successful result.' : 'Could not load AI costs.'}
      </Alert>}
      {aiCostStats && (
        <>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }} component="div">
              <StatCard
                title="Recorded Cost (All Time)"
                value={presentRecordedCost(aiCostStats.totalCostAllTime, aiCostStats.costCoverage?.allTime, t).value}
                icon={<AttachMoneyIcon sx={{ color: 'white' }} />}
                color={semantic.warning}
                subtitle={`${aiCostStats.totalQueries} calls (including system / unattributed) · ${presentRecordedCost(aiCostStats.totalCostAllTime, aiCostStats.costCoverage?.allTime, t).detail}`}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }} component="div">
              <StatCard
                title="Recorded Cost This Month"
                value={presentRecordedCost(aiCostStats.totalCostThisMonth, aiCostStats.costCoverage?.thisMonth, t).value}
                subtitle={presentRecordedCost(aiCostStats.totalCostThisMonth, aiCostStats.costCoverage?.thisMonth, t).detail}
                icon={<AttachMoneyIcon sx={{ color: 'white' }} />}
                color={semantic.info}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }} component="div">
              <StatCard
                title="Recorded Cost Today"
                value={presentRecordedCost(aiCostStats.totalCostToday, aiCostStats.costCoverage?.today, t).value}
                subtitle={presentRecordedCost(aiCostStats.totalCostToday, aiCostStats.costCoverage?.today, t).detail}
                icon={<AttachMoneyIcon sx={{ color: 'white' }} />}
                color={semantic.success}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }} component="div">
              <StatCard
                title="Unique Users"
                value={aiCostStats.uniqueUsers}
                icon={<PeopleIcon sx={{ color: 'white' }} />}
                color={brand.primary}
              />
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}
